import React, { useEffect, useRef, useState } from 'react';
import requests from "../../../../../axios/requests";
import CustomCheckbox from "../../../../../components/CustomCheckbox";
import Button from "../../../../../components/Button/Button";
import { message, notification, Modal } from "antd";
import variableLanguage from "../../../../../../utils/hookGetVariablesLanguage";
import { defaultFuncAndData } from "../../../../../../utils/defaultData";

import test_result_wrong from "../../../../../../images/icons/test_result_wrong.svg";
import test_result_ok from "../../../../../../images/icons/test_result_ok.svg";
import ImageLightbox from "../../../../../components/ImageLightbox";
import './Quiz.scss';

// Опциональная миниатюра ответа - доступна у любого типа вопроса (single/ordering/
// matching), не только у бывшего отдельного типа "выбор изображения". Клик
// открывает полный размер (без обрезки) в ImageLightbox.
const AnswerThumb = ({answer, onImageClick}) => {
    if (!answer?.img_question) return null;
    return (
        <img
            src={answer.img_question}
            alt=""
            className="quiz_answer_image_thumb"
            onClick={(e) => {
                // preventDefault - обязателен: этот thumb используется и внутри
                // CustomCheckbox.label (native htmlFor на чекбокс - клик по любому
                // содержимому label переключает чекбокс через нативное поведение
                // браузера, stopPropagation его не остановит, только preventDefault).
                e.preventDefault();
                e.stopPropagation();
                onImageClick(answer.img_question_full || answer.img_question);
            }}
        />
    );
};

const shuffle = (arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
};

const Quiz = ({questions, stage_theory_id, attempts_left, propsKey, timer, minimal_grade, welcome_text, onBackToList, onLockChange, onPassed}) => {
    const variable_16 = variableLanguage({keyPage: "lesson", keyVariable: "variable_16"});
    const variable_17 = variableLanguage({keyPage: "lesson", keyVariable: "variable_17"});
    const variable_18 = variableLanguage({keyPage: "lesson", keyVariable: "variable_18"});
    const variable_20 = variableLanguage({keyPage: "lesson", keyVariable: "variable_20"});
    const variable_23 = variableLanguage({keyPage: "lesson", keyVariable: "variable_23"});

    // Контрольный режим - ограниченное число попыток (attempts_left не null/undefined).
    // Тренировочный (attempts_left === null) - поведение не меняем, интро-экран/модалка его не касаются.
    const isControlMode = attempts_left !== null && typeof attempts_left !== "undefined";

    const [currentQuizIndx, setCurrentQuizIndx,] = useState(0);
    const [data, setData] = useState([]);
    const [quizOver, setQuizOver] = useState(false);
    const [feedbackResult, setFeedbackResult] = useState({});
    const [attempts, setAttempts] = useState(1);
    const [timeLeft, setTimeLeft] = useState(timer || null);
    const [started, setStarted] = useState(!isControlMode);
    // Реальный остаток попыток по данным сервера - обновляется ответами
    // post_quiz_start/post_quizresult (см. compile_quiz_result/start_quiz_attempt
    // в API/logic.py). Проп attempts_left - только начальное значение при
    // монтировании компонента.
    const [attemptsLeftState, setAttemptsLeftState] = useState(attempts_left);
    const [attemptId, setAttemptId] = useState(null);
    const [showStartModal, setShowStartModal] = useState(false);
    const [showUnansweredModal, setShowUnansweredModal] = useState(false);
    const [unansweredNumbers, setUnansweredNumbers] = useState([]);
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const timerIntervalRef = useRef(null);
    const sendResultRef = useRef();

    // Результаты по вопросу типа "один/несколько вариантов" - подсветка (выбран+
    // верно / выбран+неверно / не выбран+верно (эталон) / нейтрально).
    const renderChoiceResult = (q, qResult) => {
        const submitted = qResult?.answers || [];
        const correctIds = qResult?.correct_answers || [];
        const items = q.answers.map((answer) => {
            const selected = submitted.includes(answer.id);
            const isCorrectAnswer = correctIds.includes(answer.id);
            let state = "neutral";
            if (selected && isCorrectAnswer) state = "correct";
            else if (selected && !isCorrectAnswer) state = "wrong";
            else if (!selected && isCorrectAnswer) state = "missed";
            return {answer, state};
        });
        // Крупные карточки (как раньше у "Выбор изображения") - только когда у
        // вопроса реально есть картинки, иначе обычный текстовый список остаётся
        // компактным (не раздувать чисто текстовые квизы пустыми плитками).
        const hasImages = q.answers.some((a) => a.img_question);
        if (hasImages) {
            return (
                <div className="result_choice_list result_choice_list--cards">
                    {items.map(({answer, state}) => (
                        <div key={answer.id} className={`result_choice_card ${state}`}>
                            <div className="result_choice_card_media">
                                {answer.img_question &&
                                    <img
                                        src={answer.img_question}
                                        alt=""
                                        onClick={() => setLightboxSrc(answer.img_question_full || answer.img_question)}
                                    />}
                                <span className="result_icon">
                                    {state === "correct" && "✔"}
                                    {state === "wrong" && "✕"}
                                </span>
                            </div>
                            <div className="result_choice_card_text">{answer.text}</div>
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div className="result_choice_list">
                {items.map(({answer, state}) => (
                    <div key={answer.id} className={`result_choice_item ${state}`}>
                        <span className="result_icon">
                            {state === "correct" && "✔"}
                            {state === "wrong" && "✕"}
                        </span>
                        {answer.text}
                    </div>
                ))}
            </div>
        );
    };
    // Результаты по "установлению последовательности": сравниваем позицию за
    // позицией отправленный порядок с правильным, плюс отдельным рядом сам
    // правильный порядок, если есть ошибки.
    const renderOrderingResult = (q, qResult) => {
        const submittedOrder = qResult?.answers || [];
        const correctOrder = qResult?.correct_answers || [];
        return (
            <>
                <div className="result_ordering_list">
                    {submittedOrder.map((id, i) => {
                        const answer = q.answers.find((a) => a.id === id);
                        const isRight = id === correctOrder[i];
                        const expected = !isRight ? q.answers.find((a) => a.id === correctOrder[i]) : null;
                        return (
                            <div key={id} className={`result_ordering_item ${isRight ? "correct" : "wrong"}`}>
                                <span className="result_ordering_pos">{i + 1}</span>
                                {answer?.text}
                                {!isRight &&
                                    <span className="result_ordering_expected">
                                        {expected ? `Должно быть: ${expected.text}` : ""}
                                    </span>
                                }
                            </div>
                        );
                    })}
                </div>
                {!qResult?.correct &&
                    <div className="result_ordering_correct_row">
                        <div className="result_ordering_correct_label">Правильный порядок:</div>
                        {correctOrder.map((id, i) => {
                            const a = q.answers.find((x) => x.id === id);
                            return (
                                <span key={id} className="result_ordering_correct_chip">
                                    {i + 1}. {a?.text}
                                </span>
                            );
                        })}
                    </div>
                }
            </>
        );
    };
    // Результаты по "установлению пар". qResult.answers от бэкенда содержит только
    // ID строк, спаренных ВЕРНО (см. sendResult/findCorrectAnswers) - для неверных
    // попыток берём фактический выбор студента из локального data[idx].pairs, иначе
    // не сможем показать, с чем он ошибочно сопоставил левый элемент.
    const renderMatchingResult = (q, qResult, idx) => {
        const rightIds = qResult?.answers || [];
        const localPairs = data[idx]?.pairs || {};
        return (
            <div className="result_matching_list">
                {q.answers.map((row) => {
                    const isRight = rightIds.includes(row.id);
                    const userChoice = localPairs[row.id];
                    const state = isRight ? "correct" : (userChoice ? "wrong" : "unmatched");
                    return (
                        <div key={row.id} className={`result_matching_item ${state}`}>
                            <div className="result_matching_pair">
                                <span>{row.text}</span>
                                <span className="result_matching_arrow">→</span>
                                <span>{userChoice || "—"}</span>
                                <span className="result_icon">
                                    {state === "correct" && "✔"}
                                    {state === "wrong" && "✕"}
                                </span>
                            </div>
                            {state !== "correct" &&
                                <div className="result_matching_expected">
                                    Правильно: {row.text} → {row.pair_item}
                                </div>
                            }
                        </div>
                    );
                })}
            </div>
        );
    };
    const renderResultCard = (q, qResult, idx, animIndex) => {
        const type = q.type || "single";
        let body;
        if (type === "ordering") body = renderOrderingResult(q, qResult);
        else if (type === "matching") body = renderMatchingResult(q, qResult, idx);
        else body = renderChoiceResult(q, qResult);
        return (
            <div
                key={q.id}
                className={`quiz_result_card ${qResult?.correct ? "correct" : "incorrect"}`}
                style={{animationDelay: `${animIndex * 300}ms`}}
            >
                <div className="quiz_result_card_title">{idx + 1}. {q.title}</div>
                {body}
            </div>
        );
    };
    let restartAgain = () => {
        let newData = buildQuestionData(questions);
        setData(newData)
        setFeedbackResult({});
        setCurrentQuizIndx(0);
        setQuizOver(false);
        setTimeLeft(timer || null);
        setStarted(!isControlMode);
        setShowOnlyErrors(false);
    }
    let sendResult = () => {
        let findCorrectAnswers = (index) => {
            const q = data[index];
            if (q.type === "ordering") return q.pickedOrder || [];
            if (q.type === "matching") {
                return q.answers
                    .filter((a) => q.pairs?.[a.id] === a.pair_item)
                    .map((a) => a.id);
            }
            let resultIndxs = [];
            for (let i = 0; i < q.answers.length; i++)
                if (q.answers[i].value)
                    resultIndxs = [...resultIndxs, q.answers[i].id]
            return resultIndxs;
        }
        let answers = {};
        for (let i = 0; i < data.length; i++)
            answers[data[i].id] = findCorrectAnswers(i)
        let dataObj = {
            stage_theory_id,
            answers,
            attempt_id: attemptId,
        }
        requests.stages
            .post_quizresult(dataObj)
            .then((response) => {
                setQuizOver(true);
                // quiz_answers - объект {question_id: {...}}, не массив: вопросы у нас
                // перемешаны на фронте (тренировочный режим), а ключи объекта, будучи
                // числовыми id, при итерации по массиву шли бы в порядке id, а не в
                // порядке отображения - результаты искать по id вопроса, не по индексу.
                const newData = response.data;
                setFeedbackResult(newData)
                // Сразу подкрасить материал в навигаторе зелёным при переходе к
                // следующему без ожидания следующего общего рефетча стадий (тот же
                // паттерн, что setTriggerLmsEnd у Scorm.jsx - см. sendApiLms там).
                // StageTheoryUserProgress.passed уже выставлен бэкендом в
                // compile_quiz_result к этому моменту, триггерим только рефетч UI.
                if (newData?.result_json >= (minimal_grade || 100)) {
                    onPassed && onPassed();
                }
                if (typeof newData?.attempts_left !== 'undefined') {
                    setAttemptsLeftState(newData.attempts_left);
                    if (newData.attempts_left > 0) {
                        notification["warn"]({
                            message: variable_17,
                            description: `${variable_18} ${newData.attempts_left}`
                        })
                    } else {
                        message.error(variable_16)
                        setAttempts(0);
                    }
                }
            })
            .catch((error) => {
                console.log(error.response);
            });
    }
    // Всегда указывает на актуальный sendResult (с текущим data в замыкании) -
    // таймер ниже стартует один раз при монтировании и иначе вызвал бы sendResult
    // с устаревшими (пустыми) ответами.
    useEffect(() => {
        sendResultRef.current = sendResult;
    });

    const formatTime = (totalSeconds) => {
        if (totalSeconds == null) return "";
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
        if (!timer || quizOver || !started) return;
        timerIntervalRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerIntervalRef.current);
                    message.warning("Время вышло! Тест отправлен автоматически.");
                    sendResultRef.current();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => {
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        };
    }, [timer, quizOver, started]);
    // Сигнал наверх (Lesson.jsx) о том, что идёт попытка контрольного теста -
    // на время неё блокируется переход к другим материалам курса (см.
    // onLockChange проп в WebContent/ContentPage). Это UX-подстраховка -
    // реальная защита от сброса попытки уже на сервере (attempt_id,
    // start_quiz_attempt), но без этого сигнала студент мог бы случайно
    // (или ресайзом) уйти со страницы теста, не завершив попытку.
    useEffect(() => {
        if (!isControlMode) return;
        onLockChange?.(started && !quizOver);
        return () => onLockChange?.(false);
    }, [started, quizOver]);

    // Отвечен ли вопрос - зависит от типа. Принимает любой вопрос (не только
    // текущий) - нужно для навигатора по вопросам в контрольном режиме, который
    // красит кнопки по всем вопросам сразу, а не только по текущему.
    let isQuestionAnswered = (q) => {
        if (!q) return false;
        if (q.type === "ordering") return (q.pickedOrder || []).length === q.answers.length;
        if (q.type === "matching") return Object.keys(q.pairs || {}).length === q.answers.length;
        return q.answers.some((a) => a.value);
    }
    let isCurrentQuestionAnswered = () => isQuestionAnswered(data[currentQuizIndx]);
    // Прыжок на произвольный вопрос (клик по навигатору) - всегда разрешён,
    // независимо от того, отвечен ли текущий: в контрольном режиме студент должен
    // иметь возможность пропустить вопрос и вернуться к нему позже.
    let goToQuestion = (index) => {
        if (index >= 0 && index < (data?.length || 0)) setCurrentQuizIndx(index);
    }
    let finishAttempt = () => {
        if (isControlMode) {
            const unanswered = data
                .map((q, i) => ({q, i}))
                .filter(({q}) => !isQuestionAnswered(q))
                .map(({i}) => i + 1);
            if (unanswered.length > 0) {
                setUnansweredNumbers(unanswered);
                setShowUnansweredModal(true);
                return;
            }
        }
        sendResult();
    }
    let nextQuestion = () => {
        // Тренировочный режим - как раньше, нельзя пройти дальше не ответив
        // (пошаговое обучение). Контрольный - можно пропустить вопрос и вернуться
        // к нему позже через навигатор, при завершении отдельно предупредим о
        // пропущенных (см. finishAttempt), а не блокируем на каждом шаге.
        if (!isControlMode && !isCurrentQuestionAnswered()) {
            message.warning("Ответьте на вопрос полностью, прежде чем продолжить");
            return;
        }
        if (currentQuizIndx !== (data?.length - 1))
            setCurrentQuizIndx(prev => prev + 1)
        else
            finishAttempt();
    }

    // Строит локальное состояние вопросов. Для ordering/matching перемешивает
    // порядок показа, иначе студент увидит правильный ответ прямо в порядке ответов.
    // Порядок самих вопросов тоже перемешивается при каждом запуске/новой попытке -
    // и в тренировочном, и в контрольном режиме (для контрольного это происходит
    // на каждый заход в тест заново, т.к. компонент монтируется с нуля).
    const buildQuestionData = (src) => {
        let newData = JSON.parse(JSON.stringify(src));
        newData = shuffle(newData);
        for (let i = 0; i < newData.length; i++) {
            const type = newData[i].type || "single";
            if (type === "ordering") {
                newData[i].answers = shuffle(newData[i].answers);
                newData[i].pickedOrder = [];
            } else if (type === "matching") {
                newData[i].leftShuffled = shuffle(newData[i].answers.map((a) => ({id: a.id, text: a.text})));
                newData[i].rightShuffled = shuffle(newData[i].answers.map((a) => a.pair_item));
                newData[i].pairs = {};
                newData[i].selectedLeft = null;
            } else {
                for (let j = 0; j < newData[i].answers.length; j++)
                    newData[i].answers[j].value = false;
            }
        }
        return newData;
    };

    // Зависимость - не сам questions (массив), а стабильный ключ по id вопросов.
    // Родитель нередко передаёт questions новым массивом-ссылкой с тем же
    // содержимым (лишний ре-рендер после отдельного запроса/пересчёта выше по
    // дереву) - если зависеть от ссылки, эффект перезапускается и ПЕРЕмешивает
    // вопросы заново, из-за чего ученик видит один вопрос, а через секунду его
    // подменяет другой случайный (QA 2026-07-26). Перемешивать нужно только когда
    // реально изменился состав вопросов.
    const questionsKey = questions.map((q) => q.id).join(",");
    useEffect(() => {
        setData(buildQuestionData(questions))
    }, [questionsKey])

    const renderQuestion = () => {
        const q = data[currentQuizIndx];
        if (!q) return null;
        const type = q.type || "single";

        if (type === "ordering") {
            const available = q.answers.filter((a) => !(q.pickedOrder || []).includes(a.id));
            const pick = (id) => {
                let newData = [...data];
                newData[currentQuizIndx].pickedOrder = [...(newData[currentQuizIndx].pickedOrder || []), id];
                setData(newData);
            };
            const unpick = (id) => {
                let newData = [...data];
                newData[currentQuizIndx].pickedOrder = (newData[currentQuizIndx].pickedOrder || []).filter((i) => i !== id);
                setData(newData);
            };
            return (
                <div className="ordering_quiz">
                    <div className="ordering_picked">
                        {(q.pickedOrder || []).map((id, i) => {
                            const a = q.answers.find((x) => x.id === id);
                            return (
                                <div className="ordering_chip picked" key={id} onClick={() => unpick(id)}>
                                    <span className="ordering_index">{i + 1}</span>
                                    {a?.text}
                                </div>
                            );
                        })}
                    </div>
                    <div className="ordering_available">
                        {available.map((a) => (
                            <div className="ordering_chip" key={a.id} onClick={() => pick(a.id)}>
                                {a.text}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (type === "matching") {
            const selectLeft = (id) => {
                let newData = [...data];
                newData[currentQuizIndx].selectedLeft = id;
                setData(newData);
            };
            const selectRight = (rightText) => {
                if (!q.selectedLeft) return;
                let newData = [...data];
                newData[currentQuizIndx].pairs = {...newData[currentQuizIndx].pairs, [q.selectedLeft]: rightText};
                newData[currentQuizIndx].selectedLeft = null;
                setData(newData);
            };
            const unpair = (leftId) => {
                let newData = [...data];
                let pairs = {...newData[currentQuizIndx].pairs};
                delete pairs[leftId];
                newData[currentQuizIndx].pairs = pairs;
                setData(newData);
            };
            const pairedRightValues = Object.values(q.pairs || {});
            return (
                <div className="matching_quiz">
                    <div className="matching_column">
                        {q.leftShuffled.map((a) => {
                            const paired = q.pairs?.[a.id];
                            return (
                                <div
                                    key={a.id}
                                    className={`matching_chip${q.selectedLeft === a.id ? " selected" : ""}${paired ? " paired" : ""}`}
                                    onClick={() => paired ? unpair(a.id) : selectLeft(a.id)}
                                >
                                    {a.text}{paired ? ` → ${paired}` : ""}
                                </div>
                            );
                        })}
                    </div>
                    <div className="matching_column">
                        {q.rightShuffled.map((text, i) => (
                            <div
                                key={i}
                                className={`matching_chip${pairedRightValues.includes(text) ? " paired" : ""}`}
                                onClick={() => selectRight(text)}
                            >
                                {text}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Крупные карточки (образ "Выбор изображения"/CAPTCHA) - только когда у
        // вопроса реально есть картинки, иначе список остаётся обычным компактным.
        const hasImages = q.answers.some((a) => a.img_question);
        return (
            <div className={hasImages ? "variants_list variants_list--cards" : "variants_list"}>
                {q.answers.map((variant, indx) =>
                    <div
                        className={q.answers[indx].value ? "variant active" : "variant"}
                        key={variant.id}
                    >
                        <CustomCheckbox
                            value={q.answers[indx].value}
                            setValue={(e) => {
                                let newData = [...data];
                                newData[currentQuizIndx].answers[indx].value = e;
                                setData(newData)
                            }}
                            label={
                                <>
                                    <AnswerThumb answer={variant} onImageClick={setLightboxSrc}/>
                                    {variant.text}
                                </>
                            }
                            id={String(variant.id) + propsKey}
                        />
                    </div>
                )}
            </div>
        );
    };

    const isPassed = feedbackResult?.result_json >= (minimal_grade || 100);

    return (
        <>
            {attemptsLeftState !== 0
                ? <>
                    {isControlMode && !started
                        ? <div className="quiz_page quiz_intro">
                            {welcome_text &&
                                <div className="quiz_intro_welcome">{welcome_text}</div>
                            }
                            <div className="quiz_intro_params">
                                <div>Тип теста: Контрольный</div>
                                <div>
                                    Тест содержит: {questions.length}
                                    {defaultFuncAndData.declension(questions.length, [' вопрос', ' вопроса', ' вопросов'])}
                                </div>
                                <div>Осталось попыток: {attemptsLeftState}</div>
                                {timer &&
                                    <div>
                                        Время на тест: {Math.floor(timer / 60) > 0 ? `${Math.floor(timer / 60)} мин ` : ""}
                                        {timer % 60 > 0 ? `${timer % 60} сек` : ""}
                                    </div>
                                }
                                <div>Минимальный порог прохождения теста: {minimal_grade || 100}% правильных ответов</div>
                            </div>
                            <Button
                                text={"Начать тест"}
                                click={() => setShowStartModal(true)}
                                maxWidth={"200px"}
                                margin={"20px 0 20px 0"}
                                boxShadow={"none"}
                            />
                          </div>
                        : <>
                            <div className="quiz_page">
                                {quizOver
                                    ? <>
                                        {isPassed
                                            ? <div className="title_result">
                                                <img src={test_result_ok} alt="success test"/>
                                                <span>{variable_20}</span>
                                            </div>
                                            : <div className="title_result">
                                                <img src={test_result_wrong} alt="wrong test"/>
                                                <span>Вы допустили ошибки</span>
                                            </div>
                                        }
                                        {isControlMode
                                            ? <div className="quiz_result_summary">
                                                <div>Ваш результат: {feedbackResult?.result_json}%</div>
                                                <div>Правильных ответов: {feedbackResult?.count_right ?? 0} / {questions.length}</div>
                                                <div>Осталось попыток: {attemptsLeftState}</div>
                                                <div className={isPassed ? "quiz_verdict passed" : "quiz_verdict failed"}>
                                                    {isPassed ? "Сдано" : "Не сдано"}
                                                </div>
                                              </div>
                                            : <>
                                                <div className="quiz_results_header">
                                                    <div className="quiz_results_progress">
                                                        <div className="quiz_results_progress_bar">
                                                            <div
                                                                className="quiz_results_progress_fill"
                                                                style={{width: `${questions.length ? (feedbackResult?.count_right / questions.length) * 100 : 0}%`}}
                                                            />
                                                        </div>
                                                        <span>Верно: {feedbackResult?.count_right ?? 0} из {questions.length}</span>
                                                    </div>
                                                    {(feedbackResult?.count_right ?? 0) < questions.length &&
                                                        <Button
                                                            text={showOnlyErrors ? "Показать все" : "Показать только ошибки"}
                                                            click={() => setShowOnlyErrors((prev) => !prev)}
                                                            width={"auto"}
                                                            maxWidth={"240px"}
                                                            margin={"0"}
                                                            boxShadow={"none"}
                                                        />
                                                    }
                                                </div>
                                                <div className={"result_list"}>
                                                    {data
                                                        .map((q, idx) => ({q, idx, qResult: feedbackResult?.quiz_answers?.[q.id]}))
                                                        .filter(({qResult}) => !showOnlyErrors || !qResult?.correct)
                                                        .map(({q, idx, qResult}, animIndex) =>
                                                            renderResultCard(q, qResult, idx, animIndex)
                                                        )}
                                                </div>
                                              </>
                                        }
                                    </>
                                    : <>
                                        <div className="title_quiz">
                                            <span>{data[currentQuizIndx]?.title}</span>
                                            {timeLeft !== null &&
                                                <div className={timeLeft < 60 ? "quiz_timer quiz_timer-urgent" : "quiz_timer"}>
                                                    Времени осталось: {formatTime(timeLeft)}
                                                </div>
                                            }
                                            {isControlMode &&
                                                <div className="quiz_attempts_left">
                                                    Осталось попыток: {attemptsLeftState}
                                                </div>
                                            }
                                        </div>
                                        {data?.length > 0 && renderQuestion()}
                                        {/* Навигатор по вопросам - только контрольный режим (образ Staff):
                                            текущий/отвечен/не отвечен разными цветами, клик - прыжок на
                                            вопрос, чтобы вернуться и исправить ответ до сдачи теста. */}
                                        {isControlMode && data?.length > 0 &&
                                            <>
                                                <div className="quiz_navigation">
                                                    {data.map((q, i) => (
                                                        <div
                                                            key={q.id}
                                                            className={
                                                                i === currentQuizIndx
                                                                    ? "quiz_nav_btn current"
                                                                    : isQuestionAnswered(q)
                                                                        ? "quiz_nav_btn answered"
                                                                        : "quiz_nav_btn unanswered"
                                                            }
                                                            onClick={() => goToQuestion(i)}
                                                        >
                                                            {i + 1}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="quiz_finish_link" onClick={() => finishAttempt()}>
                                                    Завершить тест
                                                </div>
                                            </>
                                        }
                                    </>
                                }
                            </div>
                            {Object.values(feedbackResult).length > 0 &&
                                <>
                                    {!isControlMode && attempts > 0 &&
                                        <Button
                                            text={"Пройти заново"}
                                            click={() => restartAgain()}
                                            maxWidth={"200px"}
                                            margin={"20px 0 20px 0"}
                                            boxShadow={"none"}
                                        />
                                    }
                                    {isControlMode &&
                                        <Button
                                            text={"Вернуться к списку"}
                                            click={() => onBackToList && onBackToList()}
                                            maxWidth={"200px"}
                                            margin={"20px 0 20px 0"}
                                            boxShadow={"none"}
                                        />
                                    }
                                </>
                            }
                            {Object.values(feedbackResult).length === 0 &&
                                <Button
                                    text={currentQuizIndx === (data?.length - 1)
                                        ? "Завершить"
                                        : "Следующий вопрос"
                                    }
                                    click={() => nextQuestion()}
                                    maxWidth={"200px"}
                                    margin={"20px 0 20px 0"}
                                    boxShadow={"none"}
                                />
                            }
                            {feedbackResult?.response &&
                                <div className="recomendation_quiz">
                                    <div className="h2">
                                        {variable_23}
                                    </div>
                                    <span>{feedbackResult.response}</span>
                                </div>
                            }
                          </>
                    }
                </>
                : <div className="result_attemps-denied">
                    <img src={test_result_wrong} alt="attempts_left"/>
                    <span>{variable_16}</span>
                    {isControlMode &&
                        <Button
                            text={"Вернуться к списку"}
                            click={() => onBackToList && onBackToList()}
                            maxWidth={"200px"}
                            margin={"20px 0 0 0"}
                            boxShadow={"none"}
                        />
                    }
                </div>
            }
            <Modal
                open={showStartModal}
                footer={null}
                closable={false}
                centered
                className="quiz_start_modal"
                onCancel={() => setShowStartModal(false)}
                maskStyle={{backgroundColor: "rgba(0, 0, 0, 0.70)"}}
                bodyStyle={{padding: "25px", borderRadius: 30}}
            >
                <div className="quiz_start_modal_text">
                    Вопросы можно пропускать и возвращаться к ним через навигатор внизу экрана
                    до завершения теста. Неотвеченные вопросы будут отмечены отдельным цветом.
                </div>
                <Button
                    text={"Начать тест"}
                    click={() => {
                        // Резервируем попытку на сервере ДО открытия вопросов - идемпотентно
                        // (см. start_quiz_attempt в API/logic.py), поэтому повторный вызов
                        // (вторая вкладка, ресайз, повторный клик) не тратит лишнюю попытку,
                        // а просто возвращает уже начатую.
                        requests.stages
                            .post_quiz_start({stage_theory_id})
                            .then((response) => {
                                const data = response.data;
                                setShowStartModal(false);
                                if (data?.denied) {
                                    setAttemptsLeftState(0);
                                    message.error(variable_16);
                                    return;
                                }
                                setAttemptId(data?.attempt_id || null);
                                if (typeof data?.attempts_left !== 'undefined') {
                                    setAttemptsLeftState(data.attempts_left);
                                }
                                setStarted(true);
                            })
                            .catch((error) => {
                                console.log(error.response);
                                setShowStartModal(false);
                            });
                    }}
                    maxWidth={"200px"}
                    margin={"20px auto 0 auto"}
                    boxShadow={"none"}
                />
            </Modal>
            <Modal
                open={showUnansweredModal}
                footer={null}
                closable={false}
                centered
                className="quiz_start_modal"
                onCancel={() => setShowUnansweredModal(false)}
                maskStyle={{backgroundColor: "rgba(0, 0, 0, 0.70)"}}
                bodyStyle={{padding: "25px", borderRadius: 30}}
            >
                <div className="quiz_start_modal_text">
                    Остались неотвеченные вопросы: {unansweredNumbers.join(", ")}.
                    Завершить тест сейчас или вернуться и ответить на них?
                </div>
                <div style={{display: "flex", gap: 10, marginTop: 20}}>
                    <Button
                        text={"Вернуться к вопросам"}
                        click={() => setShowUnansweredModal(false)}
                        maxWidth={"200px"}
                        margin={"0 auto"}
                        boxShadow={"none"}
                    />
                    <Button
                        text={"Завершить тест"}
                        click={() => {
                            setShowUnansweredModal(false);
                            sendResult();
                        }}
                        maxWidth={"200px"}
                        margin={"0 auto"}
                        boxShadow={"none"}
                    />
                </div>
            </Modal>
            <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)}/>
        </>
    );
};

export default Quiz;
